// src/contexts/FinancialYearContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDocs, query, where, collection } from '../lib/offlineFirestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';
import { useAuth } from './AuthContext';
import {
  ALL_FINANCIAL_YEARS,
  filterFinancialYear,
  matchesFinancialYear,
  recordFinancialDate
} from '../utils/financialYearScope';

const FinancialYearContext = createContext();
const storageKey = (tenantId) => `coldStorage:selectedFinancialYear:${tenantId}`;
const storageObjectKey = (tenantId) => `coldStorage:selectedFinancialYearObject:${tenantId}`;
const asDate = (v) => {
  if (!v) return null;
  const x = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(x.getTime()) ? null : x;
};

export const useFinancialYear = () => {
  const context = useContext(FinancialYearContext);
  if (!context) throw new Error('useFinancialYear must be used within FinancialYearProvider');
  return context;
};

export const FinancialYearProvider = ({ children }) => {
  const { userData } = useAuth();
  const tenantId = userData?.tenantId?.id || userData?.tenantId || userData?.companyId || '';
  const [currentFinancialYear, setCurrentFinancialYearState] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(Boolean(tenantId));
  const [selectionResolvedForTenant, setSelectionResolvedForTenant] = useState('');

  const publishSelection = useCallback((fy) => {
    if (typeof window === 'undefined') return;
    window.__COLD_STORAGE_FINANCIAL_YEAR__ = fy || null;
    window.dispatchEvent(new CustomEvent('cold-storage-financial-year-changed', {
      detail: { tenantId, financialYear: fy || null }
    }));
  }, [tenantId]);

  const setCurrentFinancialYear = useCallback((fy) => {
    const selected = fy || null;
    setCurrentFinancialYearState(selected);
    if (tenantId) {
      if (selected?.id) {
        localStorage.setItem(storageKey(tenantId), selected.id);
        try {
          localStorage.setItem(storageObjectKey(tenantId), JSON.stringify({
            ...selected,
            startDate: asDate(selected.startDate)?.toISOString() || selected.startDate || null,
            endDate: asDate(selected.endDate)?.toISOString() || selected.endDate || null
          }));
        } catch (_) {}
      } else {
        // Null means the explicit "All Financial Years" filter, not "use default".
        localStorage.setItem(storageKey(tenantId), ALL_FINANCIAL_YEARS);
        localStorage.removeItem(storageObjectKey(tenantId));
      }
    }
    publishSelection(selected);
  }, [tenantId, publishSelection]);

  const fetchFinancialYears = useCallback(async (companyId) => {
    try {
      setLoading(true);
      const fyCollection = collection(db, COLLECTIONS.FINANCIAL_YEARS || 'financialYears');
      const tenantRef = doc(db, COLLECTIONS.TENANTS, companyId);
      const lookups = [
        query(fyCollection, where('tenantId', '==', tenantRef)),
        query(fyCollection, where('tenantId', '==', companyId)),
        query(fyCollection, where('tenantIdValue', '==', companyId)),
        query(fyCollection, where('companyId', '==', companyId)),
        query(fyCollection, where('companyIdValue', '==', companyId))
      ];
      const results = await Promise.allSettled(lookups.map((lookup) => getDocs(lookup)));
      const merged = new Map();
      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.docs.forEach((snap) => merged.set(snap.id, snap));
      });
      const fyData = [...merged.values()].map((snap) => ({
        id: snap.id,
        ...snap.data(),
        startDate: snap.data().startDate?.toDate?.() || snap.data().startDate,
        endDate: snap.data().endDate?.toDate?.() || snap.data().endDate
      }));
      fyData.sort((a, b) => (asDate(b.startDate)?.getTime() || 0) - (asDate(a.startDate)?.getTime() || 0));
      setFinancialYears(fyData);

      const persisted = localStorage.getItem(storageKey(companyId));
      const selected = persisted === ALL_FINANCIAL_YEARS
        ? null
        : fyData.find((fy) => fy.id === persisted) || fyData.find((fy) => fy.isCurrent === true) || fyData[0] || null;
      setCurrentFinancialYearState(selected);
      publishSelection(selected);
      setSelectionResolvedForTenant(companyId);
    } catch (error) {
      console.error('Error fetching financial years:', error);
      setFinancialYears([]);
      setCurrentFinancialYearState(null);
      publishSelection(null);
      setSelectionResolvedForTenant(companyId);
    } finally {
      setLoading(false);
    }
  }, [publishSelection]);

  useEffect(() => {
    if (tenantId) {
      setSelectionResolvedForTenant('');
      fetchFinancialYears(tenantId);
    } else {
      setFinancialYears([]);
      setCurrentFinancialYearState(null);
      setSelectionResolvedForTenant('');
      publishSelection(null);
      setLoading(false);
    }
  }, [tenantId, fetchFinancialYears, publishSelection]);

  const refreshFinancialYear = useCallback(async () => {
    if (tenantId) await fetchFinancialYears(tenantId);
  }, [tenantId, fetchFinancialYears]);

  const isInCurrentFinancialYear = useCallback((row = {}) =>
    matchesFinancialYear(row, currentFinancialYear), [currentFinancialYear]);

  const filterByFinancialYear = useCallback((rows = []) =>
    filterFinancialYear(rows, currentFinancialYear), [currentFinancialYear]);

  const scope = useMemo(() => ({
    financialYearId: currentFinancialYear?.id || '',
    financialYearName: currentFinancialYear?.name || currentFinancialYear?.year || '',
    startDate: currentFinancialYear?.startDate || null,
    endDate: currentFinancialYear?.endDate || null,
    isAllYears: !currentFinancialYear
  }), [currentFinancialYear]);

  const value = useMemo(() => ({
    currentFinancialYear,
    financialYears,
    loading,
    refreshFinancialYear,
    setCurrentFinancialYear,
    isInCurrentFinancialYear,
    filterByFinancialYear,
    scope,
    recordFinancialDate,
    isAllYears: !currentFinancialYear
  }), [
    currentFinancialYear, financialYears, loading, refreshFinancialYear,
    setCurrentFinancialYear, isInCurrentFinancialYear, filterByFinancialYear, scope
  ]);

  // Important: operational pages are mounted only after the FY selection has been
  // resolved for the authenticated tenant. This prevents their initial Firestore
  // requests from running in an accidental "all years" window during app startup.
  if (tenantId && (loading || selectionResolvedForTenant !== String(tenantId))) {
    return (
      <FinancialYearContext.Provider value={value}>
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc', color: '#475569', fontFamily: 'inherit' }}>
          <div style={{ textAlign: 'center', fontWeight: 800 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📅</div>
            Applying financial year…
          </div>
        </div>
      </FinancialYearContext.Provider>
    );
  }

  return <FinancialYearContext.Provider value={value}>{children}</FinancialYearContext.Provider>;
};
